import { useState, useEffect, useMemo, useRef, Suspense } from "react";
import { useSearchParams } from "react-router-dom";
import { useBrand } from "@/lib/brand-context";
import { useGlobalContext } from "@/context/GlobalContext";
import { BoardTag, BOARD_CONFIG, Article } from "@/types";
import { Check, AlertCircle, ChevronDown, Plus, RefreshCw, X, Sparkles, BookmarkPlus } from "lucide-react";
import AppSidebar from "@/components/layout/AppSidebar";
import KeywordDropdown from "./components/KeywordDropdown";
import RightAgentPanel from "./components/RightAgentPanel";
import ModelSelector, { DEFAULT_MODEL_ID } from "@/components/ui/ModelSelector";
import { useContentStore } from "@/store/useContentStore";
import { markdownToHtml } from "@/lib/markdownToHtml";
import { copyArticleToClipboard } from "@/lib/articleContent";
import { routeInstruction } from "./lib/intentRouter";

/* ─── Article templates ───────────────────────────────────────── */
const ARTICLE_TEMPLATES = [
  { key: "edukasi",    label: "Education (Guide/Tips)",          hint: "Focus on education, lists, how-to structure" },
  { key: "solusi",     label: "Solution & Trust (Problem/Solve)", hint: "Pain points, crisis, self-help structure" },
  { key: "kompetitor", label: "Competitor (VS/Table)",            hint: "Comparison tables, neutral evaluation" },
  { key: "konversi",   label: "Conversion",                       hint: "Numbers, pain points, soft CTA" },
  { key: "defensif",   label: "Brand Defense (Trust/Legal)",      hint: "Compliance, safety, anti-fraud" },
];

/** Intent-only strategy keys (Optimization Mode is separate). */
const INTENT_STRATEGY_KEYS = new Set([
  "edukasi",
  "solusi",
  "kompetitor",
  "konversi",
  "defensif",
]);

/* ─── Optimize-mode strategies ──────────────────────────────────────
 *
 * When the page is launched with `?mode=optimize&url=...` (typically
 * from GSC Overview → Top Pages → "Optimize"), the Strategy dropdown
 * swaps its option set to a domain that talks about *renewing* an
 * existing URL — not crafting a new piece. These keys live in their
 * own namespace and are gated by `OPTIMIZE_STRATEGY_KEYS_SET` below,
 * so the create-mode intent guard can't accidentally reset them.
 */
const OPTIMIZE_STRATEGIES = [
  { key: "fix-decay",    label: "Fix Content Decay" },
  { key: "aio-optimize", label: "AIO Optimization" },
  { key: "info-update",  label: "Information Update" },
] as const;
type OptimizeStrategyKey = (typeof OPTIMIZE_STRATEGIES)[number]["key"];
const OPTIMIZE_STRATEGY_KEYS_SET = new Set<string>(
  OPTIMIZE_STRATEGIES.map((s) => s.key),
);
const DEFAULT_OPTIMIZE_STRATEGY: OptimizeStrategyKey = "fix-decay";

/**
 * Read every diagnostic field a failed `/api/...` response can carry —
 * `error`, `detail`, `provider`, `upstreamStatus`, etc. — and produce
 * both a short headline (for the inline banner) and a multi-line dump
 * (for the expandable details, copy-to-clipboard, log forwarding).
 */
async function describeFetchError(
  res: Response,
  context: { endpoint: string; durationMs: number }
): Promise<{ short: string; full: string }> {
  const lines: string[] = [];
  lines.push(`Endpoint:   ${context.endpoint}`);
  lines.push(`HTTP:       ${res.status} ${res.statusText || ""}`.trimEnd());
  lines.push(`Duration:   ${(context.durationMs / 1000).toFixed(1)}s`);
  const fallback = res.headers.get("X-Upstream-Fallback");
  if (fallback) lines.push(`Fallback:   ${fallback}`);

  let body: unknown = null;
  let bodyText = "";
  try {
    bodyText = await res.clone().text();
  } catch { /* noop */ }
  if (bodyText) {
    try { body = JSON.parse(bodyText); } catch { body = null; }
  }

  let short = `${res.status}${res.statusText ? ` ${res.statusText}` : ""}`;
  if (body && typeof body === "object") {
    const b = body as Record<string, unknown>;
    if (typeof b.provider === "string")          lines.push(`Provider:   ${b.provider}`);
    if (typeof b.model === "string")             lines.push(`Model:      ${b.model}`);
    if (typeof b.upstreamStatus !== "undefined") {
      lines.push(`Upstream:   ${b.upstreamStatus}${b.upstreamStatusText ? ` ${b.upstreamStatusText}` : ""}`);
    }
    if (typeof b.cause === "string")             lines.push(`Cause:      ${b.cause}`);
    if (typeof b.error === "string") {
      lines.push("");
      lines.push("Error:");
      lines.push(b.error);
      short = `${res.status} — ${b.error}`;
    }
    if (typeof b.detail === "string" && b.detail) {
      lines.push("");
      lines.push("Detail:");
      lines.push(b.detail);
    }
  } else if (bodyText) {
    lines.push("");
    lines.push("Body:");
    lines.push(bodyText.slice(0, 2000));
  }

  return { short, full: lines.join("\n") };
}

const OJK_TERMS = ["bunga 0%", "pasti cair", "tanpa agunan"];

// Knowledge Base items are read dynamically from globalKws (filtered by intentBoard)

function safeDecodeURIComponent(segment: string): string {
  try {
    return decodeURIComponent(segment.replace(/\+/g, " "));
  } catch {
    return segment;
  }
}

/* ─────────────────────────────────────────────────────────────── */

function AiCreatorInner() {
  const [searchParams] = useSearchParams();
  const { config } = useBrand();
  const { keywords: globalKws, setArticles } = useGlobalContext();

  /* ── Board tags from brand config ───────────────────────────── */
  const boardTags: BoardTag[] =
    config.boardTags && config.boardTags.length > 0
      ? config.boardTags
      : Object.entries(BOARD_CONFIG).map(([k, v]) => ({ key: k, ...v }));

  const intentBoardTags: BoardTag[] = boardTags.some((t) =>
    INTENT_STRATEGY_KEYS.has(t.key)
  )
    ? boardTags.filter((t) => INTENT_STRATEGY_KEYS.has(t.key))
    : (["edukasi", "solusi", "kompetitor", "konversi", "defensif"] as const).map((key) => ({
        key,
        ...BOARD_CONFIG[key],
      }));

  /* ═══════════════════════════════════════════════════════════
     GLOBAL STORE — all content state lives here.
     Handlers write to store; the JSX reads from store.
  ═══════════════════════════════════════════════════════════ */
  const {
    /* Part 1 */
    keywords, strategy, ojkStrict, selectedTitle,
    /* Part 2 */
    outline, outlineZH, outlineGenerated, isGeneratingOutline,
    /* Part 3 */
    articleHtml, thinkingText, generationError, isGenerating,
    /* Part 4 */
    bannerCopy, videoScriptCopy, isGeneratingSocial,
    /* Agent */
    stage,
  } = useContentStore();

  const {
    removeKeyword, setKeywords,
    setStrategy, setSelectedTitle,
    updateOutline, updateOutlineZH, setOutlineGenerated, setIsGeneratingOutline,
    updateArticle, updateThinking, setGenerationError, setViolations, setIsGenerating,
    updateBannerCopy, updateVideoScript, setIsGeneratingSocial,
    pushAgentMsg, setStage, setAgentBusy,
  } = useContentStore();

  /* ── Route-driven dashboard mode ──────────────────────────────────
   *
   * GSC Overview launches us with one of two URL shapes:
   *   /ai-creator?mode=create&keyword=<query>
   *   /ai-creator?mode=optimize&url=<page-path>
   *
   * Each shape flips three pieces of the panel: TARGET URL group
   * (only visible in optimize mode), Strategy dropdown options, and
   * the primary CTA. We read params once into memoised values so
   * the dozens of downstream useMemos / JSX branches stay cheap. */
  const mode = searchParams.get("mode");
  const isOptimizeMode = mode === "optimize";
  const targetUrlParam = searchParams.get("url");
  const keywordParam = searchParams.get("keyword");
  const decodedTargetUrl = useMemo(
    () => (targetUrlParam ? safeDecodeURIComponent(targetUrlParam) : null),
    [targetUrlParam],
  );

  /* Strategy guard — different option set per mode.
   *   - optimize → must be in OPTIMIZE_STRATEGY_KEYS_SET; reset to
   *     "fix-decay" when arriving from create-mode.
   *   - create   → must be in INTENT_STRATEGY_KEYS;       reset to
   *     "edukasi" when arriving from optimize-mode.
   * Splitting the two namespaces avoids a stray "fix-decay" leaking
   * into the article generation pipeline (which only understands the
   * intent keys). */
  useEffect(() => {
    if (isOptimizeMode) {
      if (!OPTIMIZE_STRATEGY_KEYS_SET.has(strategy)) {
        setStrategy(DEFAULT_OPTIMIZE_STRATEGY);
      }
      return;
    }
    if (!INTENT_STRATEGY_KEYS.has(strategy)) setStrategy("edukasi");
  }, [isOptimizeMode, strategy, setStrategy]);

  /* One-shot keyword injection from the URL — works in BOTH modes.
   *
   * Runs strictly on mount: if `?keyword=` is present, the decoded
   * value is pushed into the global keywords store so the user sees
   * it as a pre-selected pill in the data layer. After that we step
   * out of the way — add / remove behaves normally, and re-rendering
   * (e.g. via search-param tweaks) never re-injects, because users
   * who deleted the pill clearly didn't want it back. */
  useEffect(() => {
    if (!keywordParam) return;
    const decoded = safeDecodeURIComponent(keywordParam);
    if (decoded && !keywords.includes(decoded)) {
      setKeywords([...keywords, decoded]);
    }
    // Intentional: mount-only. We never want a stale ?keyword= to
    // resurrect a pill the user just dismissed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Shorthand: push a message without manually generating id */
  function pushMsg(
    role: "agent" | "user",
    content: string,
    titles?: string[]
  ) {
    pushAgentMsg({ role, content, ...(titles ? { titles } : {}) });
  }

  const getStrategyIntentLabel = () =>
    intentBoardTags.find((t) => t.key === strategy)?.label
    ?? ARTICLE_TEMPLATES.find((t) => t.key === strategy)?.label
    ?? strategy;

  /* ── Local UI state (pure UI, not shared) ───────────────────── */
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [pendingKws, setPendingKws]         = useState<Set<string>>(new Set());
  const [reviewZH, setReviewZH]             = useState(false);
  /** Global optimization layer for all AI Creator generation calls (intent stays in `strategy` store key / labels). */
  const [optimizationMode, setOptimizationMode] = useState<"SEO" | "GEO">("SEO");
  /** Full diagnostic dump for the most recent failed generation — shown expanded in the error banner so we can debug 502/503/etc. */
  const [generationErrorDetail, setGenerationErrorDetail] = useState<string>("");
  const [errorCopied, setErrorCopied] = useState(false);

  /* ── Per-node model selection ────────────────────────────────── */
  const [selectedModelTitle,   setSelectedModelTitle]   = useState(DEFAULT_MODEL_ID);
  const [selectedModelOutline, setSelectedModelOutline] = useState(DEFAULT_MODEL_ID);
  const [selectedModelArticle, setSelectedModelArticle] = useState(DEFAULT_MODEL_ID);
  const [selectedModelSocial,  setSelectedModelSocial]  = useState(DEFAULT_MODEL_ID);
  // Step 2 inline translation toggle
  const [originalArticleHtml, setOriginalArticleHtml] = useState("");
  const [isZhPreview, setIsZhPreview]                 = useState(false);
  const [isTranslating, setIsTranslating]             = useState(false);
  // Step 3 inline translation toggle
  const [originalBannerCopy, setOriginalBannerCopy]           = useState("");
  const [originalVideoScriptCopy, setOriginalVideoScriptCopy] = useState("");
  const [isStep3ZhPreview, setIsStep3ZhPreview]               = useState(false);
  const [isStep3Translating, setIsStep3Translating]           = useState(false);
  // Step 3 individual snippet save feedback
  const [isSavingBanner, setIsSavingBanner]   = useState(false);
  const [isSavingScript, setIsSavingScript]   = useState(false);
  const [savedBannerOk,  setSavedBannerOk]    = useState(false);
  const [savedScriptOk,  setSavedScriptOk]    = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);

  /**
   * Imperatively sync store → contentEditable DOM.
   *
   * React intentionally skips re-patching dangerouslySetInnerHTML on
   * contentEditable elements (the browser's editing engine owns the DOM).
   * Any external write — whether from the generate-article stream or from
   * the Agent's dispatchToolCall — must go through this effect to actually
   * appear in the editor.
   */
  useEffect(() => {
    const el = editorRef.current;
    if (!el || isGenerating) return;
    const next = articleHtml
      || "<p style='color:#d1d5db'>Your article will appear here after clicking Generate Article. You can also start writing directly.</p>";
    // Only touch the DOM when the value genuinely changed
    if (el.innerHTML !== next) {
      el.innerHTML = next;
    }
  }, [articleHtml, isGenerating]);

  /* ── Derive primary keyword ─────────────────────────────────── */
  const primaryKeyword = keywords[0] ?? "";

  /* ── Bootstrap from URL params ──────────────────────────────── */
  useEffect(() => {
    const kws   = searchParams.get("keywords");
    const board = searchParams.get("board");
    if (kws) {
      const list = kws.split(",").map(safeDecodeURIComponent);
      setKeywords(list);
      if (list.length > 0 && stage === "keyword") {
        handleConfirmKeywordsFromList(list);
      }
    }
    if (board) {
      setStrategy(board);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  /* ─── Keyword dropdown handlers ─────────────────────────────── */
  function handleTogglePending(kw: string) {
    setPendingKws((prev) => {
      const next = new Set(prev);
      if (next.has(kw)) next.delete(kw);
      else next.add(kw);
      return next;
    });
  }

  async function handleConfirmKeywords() {
    const list = Array.from(pendingKws);
    if (!list.length) return;
    setIsDropdownOpen(false);
    await handleConfirmKeywordsFromList(list);
  }

  async function handleConfirmKeywordsFromList(list: string[]) {
    setKeywords(list);
    setPendingKws(new Set());
    setAgentBusy(true);
    pushMsg("user", `Keywords selected: ${list.join(", ")}`);
    pushMsg("agent", "✅ Got your keywords! Analyzing and generating title suggestions...");

    const kw = list[0];
    const template = ARTICLE_TEMPLATES.find((t) => t.key === strategy);

    try {
      const res = await fetch("/api/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          taskType: "write",
          modelOverride: selectedModelTitle,
          optimizationMode,
          strategy: getStrategyIntentLabel(),
          systemPrompt:
            "You are a senior Indonesian SEO strategist specializing in P2P lending (fintech) content for OJK-licensed platforms. " +
            "You write high-CTR, compliance-safe article titles in professional Bahasa Indonesia.",
          userPrompt:
            `Generate exactly 3 SEO-optimized article title options for the keyword: "${kw}". ` +
            `Content strategy: ${template?.label ?? "General SEO"}. ` +
            `Rules: each title must be under 65 characters, include the main keyword naturally, and be written in Bahasa Indonesia. ` +
            `Return ONLY a JSON array of 3 strings, no explanation. Example: ["Title 1", "Title 2", "Title 3"]`,
        }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(`API ${res.status}${errData.error ? `: ${errData.error}` : ""}`);
      }
      const data = await res.json();

      let titles: string[] = [];
      try {
        // Strip possible markdown code fences before parsing
        const cleaned = (data.content as string).replace(/```[a-z]*\n?/gi, "").trim();
        titles = JSON.parse(cleaned);
      } catch {
        // If JSON parsing fails, split by newline and clean up
        titles = (data.content as string)
          .split("\n")
          .map((l: string) => l.replace(/^[\d.\-"'\s]+/, "").replace(/[",\]]+$/, "").trim())
          .filter(Boolean)
          .slice(0, 3);
      }

      if (!titles.length) throw new Error("No titles returned");

      pushMsg(
        "agent",
        "Here are **3 title suggestions** based on your keywords. Click one to proceed:",
        titles
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error("Title generation failed:", msg);
      // Graceful fallback — still unblocks the user
      pushMsg(
        "agent",
        `⚠️ Could not reach AI (${msg}). Here are template titles — click one to continue:`,
        [
          `5 Cara Aman Menggunakan ${kw} Tanpa Risiko Finansial`,
          `Panduan Lengkap ${kw}: Hak & Perlindungan Konsumen OJK 2026`,
          `${kw} vs Alternatif Konvensional: Analisis untuk Pemula`,
        ]
      );
    } finally {
      setAgentBusy(false);
      setStage("outline");
    }
  }

  /* ─── Outline generation ────────────────────────────────────── */
  async function handleGenerateOutline() {
    if (!primaryKeyword && !outline) return;
    setIsGeneratingOutline(true);

    const template = ARTICLE_TEMPLATES.find((t) => t.key === strategy);
    const titleHint = selectedTitle ? `Approved title: "${selectedTitle}". ` : "";

    try {
      // Fire both Indonesian outline and ZH summary in parallel for speed
      const [idRes, zhRes] = await Promise.all([
        fetch("/api/generate", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            taskType: "write",
            modelOverride: selectedModelOutline,
            optimizationMode,
            strategy: getStrategyIntentLabel(),
            systemPrompt:
              "You are a senior Indonesian SEO content strategist. " +
              "Write detailed article outlines in professional Bahasa Indonesia using Markdown headings (H1/H2/H3). " +
              "Each H2 section must have 2–3 H3 sub-points with a one-sentence description.",
            userPrompt:
              `${titleHint}Create a complete SEO article outline for the keyword: "${primaryKeyword}". ` +
              `Content strategy: ${template?.label ?? "General SEO"} — ${template?.hint ?? "standard structure"}. ` +
              `The outline must have: H1 title, H2 Introduction, 3–4 H2 main sections with H3 sub-points, H2 Conclusion. ` +
              `Output in Bahasa Indonesia only.`,
          }),
        }),
        fetch("/api/generate", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            taskType: "fast",
            modelOverride: selectedModelOutline,
            optimizationMode,
            strategy: getStrategyIntentLabel(),
            systemPrompt:
              "You are a bilingual SEO strategist. Translate and summarize the following article outline into Simplified Chinese (普通话). " +
              "Preserve the Markdown heading structure. Output Chinese only.",
            userPrompt:
              `Translate this outline topic into Chinese: keyword="${primaryKeyword}", strategy="${template?.label ?? "General"}". ` +
              `Write a concise Chinese outline with the same H1/H2/H3 structure.`,
          }),
        }),
      ]);

      if (!idRes.ok) throw new Error(`Outline API ${idRes.status}`);
      const idData = await idRes.json();
      const zhData = zhRes.ok ? await zhRes.json() : { content: "" };

      updateOutline(idData.content ?? "");
      updateOutlineZH(zhData.content ?? "");
      setOutlineGenerated(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error("Outline generation failed:", msg);
      // Provide a minimal structural fallback so the user isn't blocked
      const fallback =
        `# ${primaryKeyword || "Article"}\n\n` +
        `## 1. Pendahuluan\nLatar belakang topik ini untuk pembaca Indonesia.\n\n` +
        `## 2. ${template?.hint ?? "Konten Utama"}\n### 2.1 Poin Pertama\n...\n### 2.2 Poin Kedua\n...\n\n` +
        `## 3. Kesimpulan\nRingkasan dan call-to-action.\n\n` +
        `⚠️ _AI outline generation failed (${msg}). Please edit this placeholder manually._`;
      updateOutline(fallback);
      setOutlineGenerated(true);
    } finally {
      setIsGeneratingOutline(false);
    }
  }

  /* ─── Title selection ───────────────────────────────────────── */
  async function handleSelectTitle(title: string) {
    setSelectedTitle(title);
    pushMsg("user", `Selected: "${title}"`);
    pushMsg("agent", "Perfect choice! Generating your content outline now...");
    setAgentBusy(true);

    await handleGenerateOutline();

    setAgentBusy(false);
    pushMsg(
      "agent",
      "📝 Your outline is ready on the left! Edit it directly, or send me instructions like **\"Add a section about OJK requirements\"**. When you're happy, click **Generate Article** below."
    );
  }

  /* ─── Article generation ────────────────────────────────────── */
  async function handleCreateArticle() {
    if (!outline) return;
    setStage("writing");
    setIsGenerating(true);
    setGenerationError("");
    setGenerationErrorDetail("");
    setErrorCopied(false);
    setViolations([]);
    // Reset translation toggle so the new article starts in Indonesian view
    setIsZhPreview(false);
    setOriginalArticleHtml("");
    updateArticle('<p style="color:#9ca3af;font-style:italic">🧠 AI Thinking... generating your article</p>');
    updateThinking("Analyzing keyword strategy, OJK compliance, and template structure...");
    pushMsg("agent", "🚀 Generating your full article now. This may take a moment...");

    const payload = {
      outline,
      keyword: primaryKeyword,
      board: strategy,
      template: strategy,
      strategy: getStrategyIntentLabel(),
      optimizationMode,
      globalInstructions: selectedTitle,
      modelOverride: selectedModelArticle,
    };
    /* ── Client-side hard timeout (12 min) — slightly longer than the
         server's 10-min upstream timeout so we surface the server's
         richer error message instead of an abort. */
    const CLIENT_TIMEOUT_MS = 12 * 60 * 1000;
    const ac = new AbortController();
    const clientTimer = setTimeout(() => ac.abort(), CLIENT_TIMEOUT_MS);
    const requestStartedAt = Date.now();
    let errorDetail = "";
    /* `full` is hoisted outside the try block so the catch handler can
       still recover whatever the model managed to stream before failure
       (instead of the historical behaviour of wiping the editor and
       replacing 95%-complete output with just the error banner). */
    let full = "";

    try {
      const res = await fetch("/api/generate-article", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        signal: ac.signal,
      });

      if (!res.ok) {
        const described = await describeFetchError(res, {
          endpoint: "POST /api/generate-article",
          durationMs: Date.now() - requestStartedAt,
        });
        errorDetail = described.full;
        throw new Error(described.short);
      }

      const reader = res.body?.getReader();
      const decoder = new TextDecoder();

      const ingest = (text: string) => {
        // Strip the server's heartbeat / leading byte (zero-width space).
        // It exists purely to keep the proxy socket alive and is not part
        // of the article body — must not leak into the editor or storage.
        const cleaned = text.replace(/\u200B/g, "");
        if (!cleaned) return;
        full += cleaned;
        const { thinking, article } = splitThinkingAndArticle(full);
        if (thinking) updateThinking(thinking);
        if (article) updateArticle(simpleMarkdownToHtml(article));
      };

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          ingest(decoder.decode(value, { stream: true }));
        }
        // Final flush: drain any trailing multi-byte sequence held inside
        // the decoder so the last byte of the article is never dropped.
        ingest(decoder.decode());
      }

      if (!full.trim()) throw new Error("Empty response from API");

      // Strip any legacy <thinking>/<article> wrappers before persisting
      const { article: cleanArticle } = splitThinkingAndArticle(full);
      const articleForStore = cleanArticle || full.trim();

      if (ojkStrict) {
        const found = OJK_TERMS.filter((t) =>
          articleForStore.toLowerCase().includes(t.toLowerCase())
        );
        if (found.length > 0) {
          setViolations(
            found.map((t) => `Restricted term detected: "${t}". OJK prohibits misleading guarantees.`)
          );
        }
      }

      const fallbackTitle = primaryKeyword
        ? `${primaryKeyword} — ${new Date().toLocaleDateString()}`
        : `Article ${new Date().toISOString()}`;
      // Extract real SEO title the AI wrote (e.g. "Page Title: Cara Pinjam...")
      const title = extractSeoTitle(articleForStore, fallbackTitle);
      setArticles((prev) => [
        {
          id: `a_${Date.now()}`,
          title,
          keyword: primaryKeyword,
          intentBoard: strategy,
          content: articleForStore,
          status: "draft",
          createdAt: new Date().toISOString().split("T")[0],
          wordCount: articleForStore.split(/\s+/).length,
        } as Article,
        ...prev,
      ]);

      setStage("done");
      pushMsg(
        "agent",
        "✅ Article is ready! You can edit it directly in the canvas, or send me refinement instructions. When done — save it or scroll down for social media assets."
      );

      /* ── Post-stream hooks (run only after the stream finished cleanly).
           This is the place to plug in deferred logic such as image-prompt
           extraction etc. — keep it inside the success branch so partial
           streams don't trigger downstream work. */
      // void runPostStreamHooks(articleForStore);
    } catch (err: unknown) {
      const aborted = (err instanceof DOMException && err.name === "AbortError")
        || (err instanceof Error && err.name === "AbortError");
      const msg = aborted
        ? `Request timed out after ${Math.round(CLIENT_TIMEOUT_MS / 1000)}s. The server may still be generating — please try again.`
        : (err instanceof Error ? err.message : String(err));
      const detail = errorDetail || [
        `Endpoint:   POST /api/generate-article`,
        `Duration:   ${((Date.now() - requestStartedAt) / 1000).toFixed(1)}s`,
        `Cause:      ${aborted ? "client-timeout / abort" : (err instanceof Error ? err.name : "unknown")}`,
        "",
        "Message:",
        msg,
      ].join("\n");
      setGenerationError(msg);
      setGenerationErrorDetail(detail);
      const detailHtml = detail
        .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

      // Preserve whatever the model has already streamed back — prepend an
      // error notice instead of overwriting it. Previously a late failure
      // (e.g. ECONNRESET halfway through) would wipe a 95%-complete article
      // and replace it with the error banner alone, which is exactly the
      // "article disappears / cut in half" symptom users were reporting.
      const { article: partialArticle } = splitThinkingAndArticle(full);
      const partialHtml = partialArticle ? simpleMarkdownToHtml(partialArticle) : "";
      const errorBanner =
        `<p style="color:#ef4444;font-weight:600">⚠️ Article generation failed: ${msg}</p>` +
        `<details style="margin-top:8px"><summary style="color:#6b7280;cursor:pointer;font-size:12px">Show full error</summary>` +
        `<pre style="white-space:pre-wrap;background:#fef2f2;border:1px solid #fecaca;color:#991b1b;padding:10px;border-radius:8px;font-size:12px;margin-top:6px">${detailHtml}</pre></details>` +
        `<p style="color:#6b7280;margin-top:8px">Check the server logs for matching entries, then click <strong>Generate Article</strong> again, or type your article directly in this area.</p>`;
      updateArticle(partialHtml ? `${partialHtml}\n<hr/>\n${errorBanner}` : errorBanner);
      setStage("done");
      pushMsg("agent", `⚠️ API error: ${msg}\n\n\`\`\`\n${detail}\n\`\`\``);
    } finally {
      clearTimeout(clientTimer);
      setIsGenerating(false);
    }
  }

  /* ─── Save to hub ───────────────────────────────────────────── */
  function handleSaveToHub() {
    if (!articleHtml) return;

    // Strip HTML tags to get plain text for title extraction
    const rawText = articleHtml.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    const fallbackTitle = primaryKeyword
      ? `${primaryKeyword} — ${new Date().toLocaleDateString()}`
      : `Article ${new Date().toISOString()}`;
    const title = extractSeoTitle(rawText, fallbackTitle);

    setArticles((prev) => {
      // If an article with this keyword+board was already auto-saved during
      // generation, update it in place rather than creating a duplicate.
      const existingIdx = prev.findIndex(
        (a) => a.keyword === primaryKeyword && a.intentBoard === strategy
      );
      if (existingIdx !== -1) {
        const updated = [...prev];
        updated[existingIdx] = {
          ...updated[existingIdx],
          title,
          content: articleHtml,
          wordCount: rawText.split(/\s+/).length,
        };
        return updated;
      }
      // No prior auto-save (e.g. article came entirely from Agent) → insert new
      return [
        {
          id: `a_${Date.now()}`,
          title,
          keyword: primaryKeyword,
          intentBoard: strategy,
          content: articleHtml,
          status: "draft",
          createdAt: new Date().toISOString().split("T")[0],
          wordCount: rawText.split(/\s+/).length,
        } as Article,
        ...prev,
      ];
    });

    pushMsg("agent", `💾 Saved to Content Hub with title: **"${title}"**. Find it under Generated Articles.`);
  }

  /* ─── Save individual social snippet to Content Hub ─────────── */
  /**
   * Saves a single Banner or Script snippet independently.
   * The snippet is appended to the Content Hub's Social Snippets tab.
   * Ready for backend wiring: swap the setTimeout for a real fetch call.
   */
  async function handleSaveSingleSnippet(
    type: "Copy" | "Script",
    content: string,
    setIsSaving: (v: boolean) => void,
    setSavedOk:  (v: boolean) => void,
  ) {
    if (!content.trim()) return;
    setIsSaving(true);

    try {
      // ── Stub: replace with `await fetch("/api/save-snippet", { method:"POST", ... })` ──
      await new Promise<void>((resolve) => setTimeout(resolve, 700));

      setSavedOk(true);
      setTimeout(() => setSavedOk(false), 2000);
    } finally {
      setIsSaving(false);
    }
  }

  /* ─── ZH Review — universal Chinese evaluation handler ─────── */
  /**
   * Strips HTML, takes the first 800 chars of `content`, then routes
   * a structured Chinese-review instruction through the Agent (LLM).
   * Works for any step: outline, article, or social snippets.
   */
  async function handleZHReview(content: string, stepName: string) {
    const plain = content.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    if (!plain) return;

    const excerpt = plain.slice(0, 800);
    const instruction =
      `请用中文从以下三个维度评估「${stepName}」的内容，并指出需要改进的具体点：\n` +
      `1. SEO 效果（关键词密度、标题结构、可读性）\n` +
      `2. OJK 合规性（是否存在违禁词或风险表述）\n` +
      `3. 表达流畅度（语言是否自然、专业）\n\n` +
      `内容摘要（前800字）：\n${excerpt}`;

    pushMsg("user", `[ZH Review] ${stepName}`);
    setAgentBusy(true);
    try {
      const reply = await routeInstruction(instruction, {
        stage,
        keywords,
        outline,
        articleHtml,
      });
      pushMsg("agent", reply);
    } finally {
      setAgentBusy(false);
    }
  }

  /* ─── Step 2 inline translation toggle ─────────────────────── */
  /**
   * First click  → saves current articleHtml as the original, calls
   *                /api/translate-article (streaming), updates the editor
   *                with the Chinese result, sets isZhPreview = true.
   * Second click → restores originalArticleHtml, sets isZhPreview = false.
   */
  async function handleInlineTranslate() {
    if (isZhPreview) {
      // Restore the original Indonesian article
      updateArticle(originalArticleHtml);
      setIsZhPreview(false);
      return;
    }

    if (!articleHtml || isTranslating) return;

    // Persist the original so we can toggle back
    setOriginalArticleHtml(articleHtml);
    setIsTranslating(true);
    // Show a placeholder while streaming starts
    updateArticle('<p style="color:#9ca3af;font-style:italic">🌐 Translating to Chinese…</p>');

    try {
      const res = await fetch("/api/translate-article", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ html: articleHtml }),
      });

      if (!res.ok) throw new Error(`API error: ${res.status}`);

      const reader = res.body?.getReader();
      const decoder = new TextDecoder();
      let translated = "";

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          translated += decoder.decode(value, { stream: true });
          // Stream into the editor as chunks arrive
          updateArticle(translated || '<p style="color:#9ca3af;font-style:italic">Translating…</p>');
        }
      }

      if (!translated.trim()) throw new Error("Empty translation response");

      updateArticle(translated);
      setIsZhPreview(true);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      // Restore original on failure
      updateArticle(originalArticleHtml);
      setOriginalArticleHtml("");
      pushMsg("agent", `⚠️ Translation failed: ${msg}. Original content restored.`);
    } finally {
      setIsTranslating(false);
    }
  }

  /* ─── Step 3 inline translation toggle ─────────────────────── */
  /**
   * First click  → saves both snippets, fires two parallel translation
   *                requests to /api/translate-article, writes results
   *                back into the store, sets isStep3ZhPreview = true.
   * Second click → restores originals, sets isStep3ZhPreview = false.
   */
  async function handleStep3InlineTranslate() {
    if (isStep3ZhPreview) {
      updateBannerCopy(originalBannerCopy);
      updateVideoScript(originalVideoScriptCopy);
      setIsStep3ZhPreview(false);
      return;
    }

    if ((!bannerCopy && !videoScriptCopy) || isStep3Translating) return;

    setOriginalBannerCopy(bannerCopy);
    setOriginalVideoScriptCopy(videoScriptCopy);
    setIsStep3Translating(true);

    const translate = async (text: string): Promise<string> => {
      if (!text.trim()) return text;
      const res = await fetch("/api/translate-article", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // Plain text is passed wrapped in a minimal HTML paragraph so the
        // route can stream it back; we strip the wrapper tags afterwards.
        body: JSON.stringify({ html: `<p>${text.replace(/\n/g, "</p><p>")}</p>` }),
      });
      if (!res.ok) throw new Error(`API error: ${res.status}`);
      const reader = res.body?.getReader();
      const decoder = new TextDecoder();
      let result = "";
      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          result += decoder.decode(value, { stream: true });
        }
      }
      // Strip the <p>…</p> wrappers the route adds and restore line breaks
      return result
        .replace(/<\/p><p>/g, "\n")
        .replace(/<\/?p>/g, "")
        .trim();
    };

    try {
      const [translatedBanner, translatedVideo] = await Promise.all([
        translate(bannerCopy),
        translate(videoScriptCopy),
      ]);
      updateBannerCopy(translatedBanner);
      updateVideoScript(translatedVideo);
      setIsStep3ZhPreview(true);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      // Restore originals on failure
      updateBannerCopy(originalBannerCopy);
      updateVideoScript(originalVideoScriptCopy);
      setOriginalBannerCopy("");
      setOriginalVideoScriptCopy("");
      pushMsg("agent", `⚠️ Step 3 translation failed: ${msg}. Original content restored.`);
    } finally {
      setIsStep3Translating(false);
    }
  }

  /* ─── Social media generation ───────────────────────────────── */
  async function handleGenerateSocialCopy() {
    if (!articleHtml) return;
    setIsGeneratingSocial(true);
    // Reset translation toggle so freshly generated snippets start in Indonesian view
    setIsStep3ZhPreview(false);
    setOriginalBannerCopy("");
    setOriginalVideoScriptCopy("");
    updateBannerCopy("");
    updateVideoScript("");

    // Strip HTML to plain text for the prompt (max 1500 chars to stay within token budget)
    const articlePlain = articleHtml
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 1500);

    const kw = primaryKeyword || "topik ini";

    try {
      const [bannerRes, videoRes] = await Promise.all([
        fetch("/api/generate", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            taskType: "write",
            modelOverride: selectedModelSocial,
            optimizationMode,
            strategy: getStrategyIntentLabel(),
            systemPrompt:
              "You are a social media copywriter specializing in Indonesian fintech content. " +
              "Write engaging, OJK-compliant copy in professional Bahasa Indonesia. " +
              "Never make false guarantees (e.g. 'pasti cair', 'bunga 0%'). " +
              "Output only the copy text — no preamble, no labels.",
            userPrompt:
              `Based on this article about "${kw}", write a Facebook/LinkedIn post:\n\n` +
              `Article summary: ${articlePlain}\n\n` +
              `Requirements: 3–4 sentences, include 3 key takeaways with ✅ bullets, end with 3–4 relevant hashtags. ` +
              `Tone: professional yet approachable. Max 300 characters (excluding hashtags).`,
          }),
        }),
        fetch("/api/generate", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            taskType: "write",
            modelOverride: selectedModelSocial,
            optimizationMode,
            strategy: getStrategyIntentLabel(),
            systemPrompt:
              "You are a short-video script writer for Indonesian fintech TikTok/Reels content. " +
              "Write punchy, hook-driven scripts in Bahasa Indonesia. " +
              "Always comply with OJK regulations — no misleading claims. " +
              "Output only the script — no preamble, no labels.",
            userPrompt:
              `Based on this article about "${kw}", write a 60-second TikTok/Reels script:\n\n` +
              `Article summary: ${articlePlain}\n\n` +
              `Structure:\n` +
              `🎣 Hook (0–3s): attention-grabbing question or shocking fact\n` +
              `🎯 Key Points (3–50s): 3 bullet points with emojis\n` +
              `💥 CTA (50–60s): follow/subscribe call-to-action`,
          }),
        }),
      ]);

      if (!bannerRes.ok) throw new Error(`Banner API ${bannerRes.status}`);
      if (!videoRes.ok)  throw new Error(`Video API ${videoRes.status}`);

      const [bannerData, videoData] = await Promise.all([
        bannerRes.json(),
        videoRes.json(),
      ]);

      updateBannerCopy(bannerData.content ?? "");
      updateVideoScript(videoData.content ?? "");
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error("Social copy generation failed:", msg);
      updateBannerCopy(`⚠️ Social copy generation failed: ${msg}. Please try again.`);
      updateVideoScript("");
    } finally {
      setIsGeneratingSocial(false);
    }
  }

  /* ─── Keyword dropdown groups ───────────────────────────────── */
  // Knowledge Base = keywords that have been processed (have an intentBoard assigned)
  const kbItems = globalKws
    .filter((k) => k.intentBoard && k.intentBoard !== "unclassified")
    .map((k) => k.keyword)
    .filter(Boolean);
  const seedItems = globalKws.map((k) => k.keyword).filter(Boolean);
  const kwGroups = [
    { label: "Seed Keywords", items: seedItems },
    ...(kbItems.length ? [{ label: "Knowledge Base", items: kbItems }] : []),
  ];

  /* ────────────────────────────────────────────────────────────── */
  return (
    <div className="flex h-screen w-full overflow-hidden bg-[#F7F7FB]">

      {/* ═══ LEFT: shared sidebar (untouched) ═══ */}
      <AppSidebar />

      {/* ═══ CENTER: 4-step workflow ═══
          Part 1 sits OUTSIDE the scroll region so model/keyword popovers are not
          clipped by overflow-y-auto; steps 2+ scroll normally. */}
      <div className="flex-1 min-w-0 flex min-h-0 flex-col overflow-hidden">
        <div className="shrink-0 overflow-visible px-6 pt-8">
          <div className="max-w-3xl mx-auto">
          {/* ──────────────────────────────────────────────────────
              PART 1 — Setup & Settings
          ────────────────────────────────────────────────────── */}
          <div className="relative z-[120] mb-6 bg-white rounded-xl border border-gray-200 shadow-sm p-5 overflow-visible">

            {/* ── Upper config block — full control / data separation ──
             *
             * Outer flex-col stacks two semantic rows:
             *   1. items-end row (h-9 baseline)  — primary controls
             *      (optional TARGET URL in optimize mode, KEYWORDS,
             *      MODE, STRATEGY) live here and stay locked on one
             *      baseline. OJK Strict used to ride this row but was
             *      moved to global Settings — it's a brand-wide
             *      compliance flag, not a per-document toggle.
             *   2. flex-wrap row (conditional)   — selected keyword
             *      pills render here as a *sibling* of the control row,
             *      so no matter how many tags the user picks, the
             *      primary controls never lose their shared bottom
             *      alignment. */}
            <div className="flex flex-col gap-4">

              {/* ─ Context row — Target URL (optimize mode only) ──
               * Promoted from inside the items-end row to its own
               * track above the core controls. With OJK Strict gone
               * and only KEYWORDS / MODE / STRATEGY left in the
               * control row, packing all four into one line still
               * crushed long URLs at narrow viewports — so we adopt
               * the "context block independent" pattern:
               *
               *   Row 1  Context        → TARGET URL    (this block)
               *   Row 2  Core controls  → KEYWORDS · MODE · STRATEGY
               *   Row 3  Data           → selected keyword pills
               *
               * Each row is its own flex track, so a long URL can
               * breathe (w-full / max-w-3xl) without ever feeding
               * back into the control row's column widths. */}
              {isOptimizeMode && (
                <div className="relative z-0 flex w-full max-w-3xl flex-col gap-2">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Target URL
                  </span>
                  <div
                    className="flex h-9 w-full items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3"
                    title={decodedTargetUrl ?? "—"}
                  >
                    <span className="truncate text-sm text-gray-600">
                      {decodedTargetUrl ?? "—"}
                    </span>
                    <span className="ml-auto flex shrink-0 items-center gap-1 text-[10.5px] font-semibold text-emerald-600">
                      <Check size={11} strokeWidth={3} />
                      Fetched
                    </span>
                  </div>
                </div>
              )}

              {/* Control layer — items-end pins every form group's
               *  bottom edge on one horizontal line. With TARGET URL
               *  promoted to the context row above, this track now
               *  only ever carries 3 controls, which fit comfortably
               *  even at narrow viewports. */}
              <div className="flex flex-row items-end gap-6">

              {/* ─ Keywords ──────────────────────────────────────────
               * Only Label + control sub-row live here now. Pill chips
               * are rendered as a sibling row below this items-end
               * container (see "Data layer" further down) so they can
               * never push KEYWORDS' control row out of alignment with
               * MODE / STRATEGY. */}
              <div className="relative z-0 flex flex-col gap-2">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Keywords
                </span>

                {/* Control layer */}
                <div className="relative flex flex-row items-center gap-2 h-9">
                  {/* Sonnet 4.6 trigger — descendant variants force the
                   *  global ModelSelector's inline-variant button to wear
                   *  the unified form-field skin without touching the
                   *  shared component file. */}
                  <ModelSelector
                    value={selectedModelTitle}
                    onChange={setSelectedModelTitle}
                    direction="down"
                    className={
                      "relative z-[50] shrink-0 " +
                      "[&>button]:!h-9 [&>button]:!min-w-[160px] [&>button]:!justify-between " +
                      "[&>button]:!bg-white [&>button]:hover:!bg-gray-50 " +
                      "[&>button]:!border [&>button]:!border-gray-200 [&>button]:!rounded-md " +
                      "[&>button]:!text-sm [&>button]:!text-gray-700 [&>button]:hover:!text-gray-700 " +
                      "[&>button]:!font-medium [&>button]:!px-3 [&>button]:!py-0 [&>button]:!gap-2 " +
                      "[&>button>span]:!max-w-none"
                    }
                  />
                  <button
                    onClick={() => setIsDropdownOpen((v) => !v)}
                    className="relative z-0 h-9 flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-3 rounded-md transition-colors border border-dashed border-indigo-300"
                  >
                    <Plus size={11} strokeWidth={3} />
                    Add
                  </button>

                  {isDropdownOpen && (
                    <div className="absolute top-full left-0 z-[190] mt-2">
                      <KeywordDropdown
                        groups={kwGroups}
                        pending={pendingKws}
                        onToggle={handleTogglePending}
                        onConfirm={handleConfirmKeywords}
                        onClose={() => setIsDropdownOpen(false)}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* ─ Mode ────────────────────────────────────────────── */}
              <div className="relative z-0 flex flex-col gap-2 shrink-0">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Mode
                </span>
                <div
                  className="flex h-9 items-stretch rounded-md border border-gray-200 bg-gray-100/90 p-0.5 gap-0.5"
                  role="group"
                  aria-label="Optimization mode"
                >
                  <button
                    type="button"
                    onClick={() => setOptimizationMode("SEO")}
                    className={`h-full flex items-center justify-center rounded-md px-3 text-xs font-bold transition-colors ${
                      optimizationMode === "SEO"
                        ? "bg-white text-indigo-700 shadow-sm ring-1 ring-gray-200/80"
                        : "text-gray-500 hover:text-gray-800"
                    }`}
                  >
                    SEO
                  </button>
                  <button
                    type="button"
                    onClick={() => setOptimizationMode("GEO")}
                    className={`h-full flex items-center justify-center rounded-md px-3 text-xs font-bold transition-colors ${
                      optimizationMode === "GEO"
                        ? "bg-white text-indigo-700 shadow-sm ring-1 ring-gray-200/80"
                        : "text-gray-500 hover:text-gray-800"
                    }`}
                  >
                    GEO
                  </button>
                </div>
              </div>

              {/* ─ Strategy ──────────────────────────────────────────
               * Native <select> stripped of its OS chrome via
               * appearance-none, then re-skinned to match the Sonnet 4.6
               * trigger byte-for-byte. A separate ChevronDown SVG is
               * overlaid since native selects no longer render one. */}
              <div className="relative z-0 flex flex-col gap-2">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Strategy
                </span>
                <div className="relative">
                  <select
                    value={strategy}
                    onChange={(e) => setStrategy(e.target.value)}
                    className="appearance-none h-9 min-w-[160px] bg-white border border-gray-200 rounded-md text-sm font-medium text-gray-700 px-3 pr-9 hover:bg-gray-50 transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-200"
                  >
                    {/* Strategy options are mode-dependent — see
                     *  OPTIMIZE_STRATEGIES / intentBoardTags. The
                     *  guard useEffect above re-aligns `strategy` to
                     *  whichever namespace the current mode uses. */}
                    {isOptimizeMode
                      ? OPTIMIZE_STRATEGIES.map((s) => (
                          <option key={s.key} value={s.key}>
                            {s.label}
                          </option>
                        ))
                      : intentBoardTags.map((tag) => (
                          <option key={tag.key} value={tag.key}>
                            {tag.label}
                          </option>
                        ))}
                  </select>
                  <ChevronDown
                    size={14}
                    strokeWidth={2}
                    className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                  />
                </div>
              </div>

              {/* OJK Strict moved to Settings → it's a global brand-
               *  compliance setting, not a per-document switch. The
               *  underlying `ojkStrict` flag is still read from the
               *  shared store by the post-generation OJK_TERMS guard
               *  in handleCreateArticle, so removing the UI here is
               *  purely a presentation change. */}

              </div>

              {/* Data layer — selected keyword pills.
               * Sibling of the control row, NOT a child of any column.
               * That's the whole trick: a tag's height can never feed
               * back into the items-end flex container above. */}
              {keywords.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {keywords.map((kw) => (
                    <span
                      key={kw}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 text-indigo-700 text-xs font-medium rounded-full border border-indigo-100"
                    >
                      {kw}
                      <button
                        type="button"
                        onClick={() => removeKeyword(kw)}
                        aria-label={`Remove ${kw}`}
                        className="hover:bg-indigo-200 rounded-full p-0.5 transition-colors"
                      >
                        <X size={10} strokeWidth={3} />
                      </button>
                    </span>
                  ))}
                </div>
              )}

            </div>

            {/* ── Action row ── separated by a hairline border to give
             * Generate Draft real visual weight as the panel's primary CTA. */}
            <div className="relative z-[60] mt-4 pt-4 border-t border-gray-100 flex items-center justify-end gap-4">
              <ModelSelector
                value={selectedModelArticle}
                onChange={setSelectedModelArticle}
                direction="down"
              />
              <button
                onClick={handleCreateArticle}
                disabled={isGenerating || isGeneratingOutline}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-bold rounded-xl transition-colors shadow-sm shadow-indigo-200 whitespace-nowrap"
              >
                {/* Primary CTA reshapes with the dashboard mode:
                 *   create   → Sparkles + "Generate Draft"
                 *   optimize → RefreshCw + "Run Optimization"
                 * (The in-flight spinner short-circuits both.) */}
                {(isGenerating || isGeneratingOutline) ? (
                  <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : isOptimizeMode ? (
                  <RefreshCw size={13} />
                ) : (
                  <Sparkles size={13} />
                )}
                {isOptimizeMode ? "Run Optimization" : "Generate Draft"}
              </button>
            </div>

            {/* Selected title strip */}
            {selectedTitle && (
              <div className="flex items-start gap-3 bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-2.5 mt-4">
                <span className="text-[10px] font-bold text-indigo-400 bg-white rounded px-1.5 py-0.5 flex-shrink-0 mt-0.5">
                  Title
                </span>
                <p className="text-sm font-semibold text-indigo-800 leading-snug">{selectedTitle}</p>
              </div>
            )}
          </div>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
          <div className="max-w-3xl mx-auto flex flex-col gap-6 px-6 pb-8 pt-0">

          {/* ──────────────────────────────────────────────────────
              PART 2 — Step 1: Content Outline
          ────────────────────────────────────────────────────── */}
          <div className="relative z-0 bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-[10px] font-bold text-indigo-400 tracking-widest uppercase mb-0.5">Step 1</p>
                <h2 className="text-base font-bold text-gray-900">Content Outline</h2>
              </div>
              <div className="flex items-center gap-2">
                {outlineGenerated && (
                  <>
                    <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                      <Check size={11} /> Generated
                    </span>
                    <button
                      onClick={() => {
                        // Toggle textarea between EN/ZH outline
                        setReviewZH((v) => !v);
                        // Also send to Agent for Chinese evaluation (only when turning on)
                        if (!reviewZH) handleZHReview(outline, "Step 1: Content Outline");
                      }}
                      className={`text-xs px-2.5 py-1 rounded-lg transition-colors ${
                        reviewZH ? "bg-indigo-100 text-indigo-700" : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                      }`}
                    >
                      ZH Review
                    </button>
                  </>
                )}
                <div className="flex items-center gap-1.5">
                  <ModelSelector
                    value={selectedModelOutline}
                    onChange={setSelectedModelOutline}
                    direction="down"
                  />
                  <button
                    onClick={handleGenerateOutline}
                    disabled={isGeneratingOutline}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 hover:bg-gray-700 disabled:opacity-50 text-white text-xs font-semibold rounded-lg transition-colors"
                  >
                    {isGeneratingOutline && (
                      <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    )}
                    {isGeneratingOutline ? "Generating..." : outlineGenerated ? "Regenerate" : "Generate Outline"}
                  </button>
                </div>
              </div>
            </div>

            <textarea
              value={reviewZH ? outlineZH : outline}
              onChange={(e) =>
                reviewZH ? updateOutlineZH(e.target.value) : updateOutline(e.target.value)
              }
              rows={12}
              placeholder="Your content outline will appear here after generation, or you can type/paste it manually."
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-indigo-300 resize-none bg-gray-50"
            />
          </div>

          {/* ──────────────────────────────────────────────────────
              PART 3 — Step 2: Full Article
          ────────────────────────────────────────────────────── */}
          <div className="relative z-0 bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <p className="text-[10px] font-bold text-indigo-400 tracking-widest uppercase mb-0.5">Step 2</p>
                <h2 className="text-base font-bold text-gray-900">Full Article</h2>
              </div>
              <div className="flex items-center gap-3">
                {isGenerating && (
                  <span className="flex items-center gap-1.5 text-xs text-indigo-600 font-semibold">
                    <span className="w-3 h-3 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                    Generating…
                  </span>
                )}
                {articleHtml && (
                  <button
                    onClick={handleInlineTranslate}
                    disabled={isTranslating}
                    className={`text-xs px-2.5 py-1 rounded-lg transition-colors disabled:opacity-50 ${
                      isZhPreview
                        ? "bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
                        : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                    }`}
                  >
                    {isTranslating
                      ? "Translating…"
                      : isZhPreview
                      ? "Show Original (ID)"
                      : "ZH Preview"}
                  </button>
                )}
                <div className="flex items-center gap-1.5">
                  <ModelSelector
                    value={selectedModelArticle}
                    onChange={setSelectedModelArticle}
                    direction="down"
                  />
                  <button
                    onClick={handleCreateArticle}
                    disabled={isGenerating || !outline}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-semibold rounded-lg transition-colors"
                  >
                    🚀 {isGenerating ? "Writing…" : "Generate Article"}
                  </button>
                </div>
                {articleHtml && (
                  <button
                    onClick={handleSaveToHub}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-lg transition-colors"
                  >
                    💾 Save
                  </button>
                )}
              </div>
            </div>

            {/* Formatting toolbar */}
            <div className="flex items-center gap-1.5 px-6 py-3 border-b border-gray-100 bg-gray-50">
              {["B", "I", "H2", "H3"].map((cmd) => (
                <button
                  key={cmd}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    document.execCommand(
                      cmd === "B" ? "bold" : cmd === "I" ? "italic" : "formatBlock",
                      false,
                      cmd === "H2" ? "h2" : cmd === "H3" ? "h3" : undefined
                    );
                  }}
                  className="px-2.5 py-1 text-xs font-bold bg-white border border-gray-200 hover:bg-gray-100 rounded text-gray-700 transition-colors"
                >
                  {cmd}
                </button>
              ))}
            </div>

            {/* AI thinking */}
            {thinkingText && (
              <details className="mx-6 mt-4 bg-amber-50 rounded-xl border border-amber-100 text-xs">
                <summary className="px-4 py-2.5 cursor-pointer font-semibold text-amber-700">
                  🧠 AI Thinking Process (click to expand)
                </summary>
                <pre className="px-4 pb-3 text-amber-600 whitespace-pre-wrap leading-relaxed">{thinkingText}</pre>
              </details>
            )}

            {/* Error banner — full diagnostic dump, copyable */}
            {generationError && (
              <div className="bg-red-50 border border-red-200 rounded-xl mx-6 mt-4 px-4 py-3 text-sm text-red-700">
                <div className="flex items-start gap-2">
                  <AlertCircle size={14} className="mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold break-words">API error: {generationError}</div>
                    {generationErrorDetail && (
                      <details className="mt-2">
                        <summary className="cursor-pointer text-xs text-red-600 hover:text-red-800 select-none">
                          Show full error details
                        </summary>
                        <pre className="mt-2 max-h-72 overflow-auto rounded-lg bg-white border border-red-100 p-3 text-[11px] leading-relaxed text-red-800 whitespace-pre-wrap break-words">
{generationErrorDetail}
                        </pre>
                        <div className="mt-2 flex items-center gap-2">
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                await navigator.clipboard.writeText(generationErrorDetail);
                                setErrorCopied(true);
                                setTimeout(() => setErrorCopied(false), 1500);
                              } catch { /* noop */ }
                            }}
                            className="text-[11px] px-2 py-1 rounded-md bg-white border border-red-200 text-red-700 hover:bg-red-100 transition-colors"
                          >
                            {errorCopied ? "Copied" : "Copy error"}
                          </button>
                        </div>
                      </details>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Rich-text editor — innerHTML managed imperatively via useEffect */}
            <div
              ref={editorRef}
              contentEditable={!isGenerating}
              suppressContentEditableWarning
              className="min-h-[400px] p-6 focus:outline-none prose prose-sm max-w-none text-gray-800 leading-relaxed"
            />
          </div>

          {/* ──────────────────────────────────────────────────────
              PART 4 — Step 3: Social Media Snippets
          ────────────────────────────────────────────────────── */}
          <div className="relative z-0 bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <p className="text-[10px] font-bold text-indigo-400 tracking-widest uppercase mb-0.5">Step 3</p>
                <h2 className="text-base font-bold text-gray-900">Social Media Snippets</h2>
                <p className="text-xs text-gray-400 mt-0.5">
                  Generate banner copy and video scripts from your article — or ask the Agent on the right.
                </p>
              </div>
              <div className="flex items-center gap-2">
                {(bannerCopy || videoScriptCopy) && (
                  <button
                    onClick={handleStep3InlineTranslate}
                    disabled={isStep3Translating}
                    className={`text-xs px-2.5 py-1 rounded-lg transition-colors disabled:opacity-50 ${
                      isStep3ZhPreview
                        ? "bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
                        : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                    }`}
                  >
                    {isStep3Translating
                      ? "Translating…"
                      : isStep3ZhPreview
                      ? "Show Original (ID)"
                      : "ZH Preview"}
                  </button>
                )}
                <div className="flex items-center gap-1.5">
                  <ModelSelector
                    value={selectedModelSocial}
                    onChange={setSelectedModelSocial}
                    direction="down"
                  />
                  <button
                    onClick={handleGenerateSocialCopy}
                    disabled={isGeneratingSocial || !articleHtml}
                    className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-bold rounded-lg transition-colors whitespace-nowrap"
                  >
                    {isGeneratingSocial ? (
                      <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : "⚡️"}
                    {isGeneratingSocial ? "Generating..." : "Generate Snippets"}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-5">
              {/* Banner / Ad Copy */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-5 h-5 rounded bg-blue-100 flex items-center justify-center text-blue-600 text-[10px] font-black flex-shrink-0">f</span>
                  <label className="text-xs font-bold text-gray-600 uppercase tracking-wider">
                    Banner / Ad Copy
                  </label>
                  <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">Facebook · LinkedIn</span>
                  {/* Right-aligned actions */}
                  <div className="ml-auto flex items-center gap-1.5">
                    {bannerCopy && <CopyButton text={bannerCopy} />}
                    {bannerCopy && (
                      <SaveSnippetButton
                        isSaving={isSavingBanner}
                        savedOk={savedBannerOk}
                        onClick={() => handleSaveSingleSnippet("Copy", bannerCopy, setIsSavingBanner, setSavedBannerOk)}
                      />
                    )}
                  </div>
                </div>
                <textarea
                  value={bannerCopy}
                  onChange={(e) => updateBannerCopy(e.target.value)}
                  rows={5}
                  placeholder="Banner/ad copy will appear here — or type: 'Write banner copy' in the Agent panel."
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-indigo-300 resize-none bg-gray-50"
                />
              </div>

              {/* Video Script */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-5 h-5 rounded bg-gray-900 flex items-center justify-center text-white text-[10px] font-black flex-shrink-0">▶</span>
                  <label className="text-xs font-bold text-gray-600 uppercase tracking-wider">
                    Video Script / Hook
                  </label>
                  <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">TikTok · Reels · YouTube Shorts</span>
                  {/* Right-aligned actions */}
                  <div className="ml-auto flex items-center gap-1.5">
                    {videoScriptCopy && <CopyButton text={videoScriptCopy} />}
                    {videoScriptCopy && (
                      <SaveSnippetButton
                        isSaving={isSavingScript}
                        savedOk={savedScriptOk}
                        onClick={() => handleSaveSingleSnippet("Script", videoScriptCopy, setIsSavingScript, setSavedScriptOk)}
                      />
                    )}
                  </div>
                </div>
                <textarea
                  value={videoScriptCopy}
                  onChange={(e) => updateVideoScript(e.target.value)}
                  rows={7}
                  placeholder="Video hook + script will appear here — or type: 'Write TikTok script' in the Agent panel."
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-indigo-300 resize-none bg-gray-50 font-mono"
                />
              </div>
            </div>
          </div>

          <div className="h-10" />
        </div>
      </div>
      </div>

      {/* ═══ RIGHT: AI agent panel ═══
           Props are minimal — state comes from the store internally.
           Only complex async handlers are still passed as props.    */}
      <RightAgentPanel
        onSelectTitle={handleSelectTitle}
        onSave={handleSaveToHub}
      />
    </div>
  );
}

/* ─── Save-snippet ghost button ──────────────────────────────── */
function SaveSnippetButton({
  isSaving,
  savedOk,
  onClick,
}: {
  isSaving: boolean;
  savedOk: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      disabled={isSaving || savedOk}
      title="Save snippet to Content Hub"
      className={`flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-lg transition-all disabled:cursor-not-allowed ${
        savedOk
          ? "bg-green-100 text-green-700"
          : "bg-gray-100 hover:bg-blue-50 text-gray-500 hover:text-blue-600"
      }`}
    >
      {isSaving ? (
        <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
      ) : savedOk ? (
        <Check size={12} strokeWidth={3} />
      ) : (
        <BookmarkPlus size={12} />
      )}
      {isSaving ? "Saving…" : savedOk ? "Saved!" : "Save"}
    </button>
  );
}

/* ─── Inline copy button ──────────────────────────────────────── */
/**
 * Rich-text copy button: pushes BOTH text/html (with inline styles
 * for headings, bold, lists) and text/plain to the clipboard so
 * pasting into Word / Google Docs / WordPress preserves formatting.
 */
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  async function handleCopy() {
    const ok = await copyArticleToClipboard(text);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }
  return (
    <button
      onClick={() => { void handleCopy(); }}
      className={`flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-lg transition-all ${
        copied ? "bg-green-100 text-green-700" : "bg-gray-100 hover:bg-gray-200 text-gray-600"
      }`}
    >
      {copied ? "✓ Copied!" : "⎘ Copy"}
    </button>
  );
}

/* ─── Markdown → HTML (alias to shared util) ─────────────────── */
const simpleMarkdownToHtml = markdownToHtml;

/* ─── Defensive: separate any legacy <thinking>/<article> tags ── */
/**
 * Older versions of the article generator wrapped output in
 * <thinking>…</thinking><article>…</article>. The new prompt no longer
 * emits these tags, but we keep this splitter so:
 *   1. mid-stream chunks that briefly contain <thinking> are not shown,
 *   2. articles persisted from past sessions still render cleanly.
 *
 * Returns the thinking block (if any) and the cleaned article body.
 */
function splitThinkingAndArticle(raw: string): { thinking: string; article: string } {
  if (!raw) return { thinking: "", article: "" };
  const lower    = raw.toLowerCase();
  const openIdx  = lower.indexOf("<thinking>");
  const stripArticleTags = (s: string) => s.replace(/<\/?article>/gi, "").trim();

  if (openIdx === -1) {
    return { thinking: "", article: stripArticleTags(raw) };
  }

  const closeIdx = lower.indexOf("</thinking>", openIdx);
  if (closeIdx === -1) {
    // Streaming hasn't reached </thinking> yet — keep article = pre-thinking only
    return {
      thinking: raw.slice(openIdx + "<thinking>".length).trim(),
      article:  stripArticleTags(raw.slice(0, openIdx)),
    };
  }
  const thinking = raw.slice(openIdx + "<thinking>".length, closeIdx).trim();
  const article  = stripArticleTags(
    raw.slice(0, openIdx) + raw.slice(closeIdx + "</thinking>".length)
  );
  return { thinking, article };
}

/* ─── Extract SEO title from raw AI output ────────────────────── */
/**
 * The AI prefixes its article with metadata lines such as:
 *   Page Title: Cara Pinjam Uang di Easycash dengan Bunga Rendah
 * This helper extracts that line and falls back to `fallback` when
 * the pattern is absent (e.g. the article is entirely user-written).
 */
function extractSeoTitle(rawText: string, fallback: string): string {
  const match = rawText.match(/Page\s*Title\s*[:\-]\s*(.+)/i);
  return match ? match[1].trim() : fallback;
}

/* ─── Root export ─────────────────────────────────────────────── */
export default function AiCreatorPage() {
  return (
    <Suspense fallback={
      <div className="flex h-screen w-full items-center justify-center bg-[#F7F7FB] text-gray-400">
        Loading…
      </div>
    }>
      <AiCreatorInner />
    </Suspense>
  );
}
